import type { KeycloakTokenParsed } from 'keycloak-js';
import type Keycloak from 'keycloak-js';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import type { MuteChatGlobalDTO, UserResultDTO } from '@nora-monorepo/nora-api-client';
import {
  checkIsMuted,
  useApiStore,
  useGenericUserStore,
} from '@nora-monorepo/ui-shared';

export const useChatUserStore = defineStore('chatUserStore', () => {
  const keycloak = ref<Keycloak>();
  const genericUserStore = useGenericUserStore();
  const { user } = storeToRefs(genericUserStore);
  const token = ref<string>();
  const tokenParsed = ref<KeycloakTokenParsed>();
  const api = useApiStore();

  const getUserId = computed(() => {
    return user.value?.id;
  });

  const isMuted = ref<boolean>();
  const muteInterval = ref<ReturnType<typeof setInterval>>();

  function updateMuteStatus(): void {
    isMuted.value = checkIsMuted(
      user.value?.chatMutedIndefinite,
      user.value?.chatMutedUntil,
    );

    if (!isMuted.value && user.value) {
      user.value.chatMutedUntil = undefined;
      user.value.chatMutedIndefinite = false;
    }

    if (!isMuted.value && muteInterval.value) {
      stopMuteInterval();
    }
  }

  function startMuteInterval(): ReturnType<typeof setInterval> {
    const intervalId = setInterval(() => {
      updateMuteStatus();
    }, 1000 * 60);

    return intervalId;
  }

  function stopMuteInterval(): void {
    if (muteInterval.value) {
      clearInterval(muteInterval.value);
      muteInterval.value = undefined;
    }
  }

  async function setGlobalMute(
    payload: MuteChatGlobalDTO,
    excludeAPICall?: boolean,
  ): Promise<void> {
    if (!user.value) {
      return;
    }

    isMuted.value = checkIsMuted(
      payload.chatMutedIndefinite,
      payload.chatMutedUntil,
    );

    if (!isMuted.value && muteInterval.value) {
      clearInterval(muteInterval.value);

      muteInterval.value = undefined;
    }

    if (isMuted.value && !muteInterval.value) {
      muteInterval.value = startMuteInterval();
    }

    user.value = {
      ...user.value,
      chatMutedUntil: payload.chatMutedUntil
        ? new Date(payload.chatMutedUntil)
        : undefined,
      chatMutedIndefinite: payload.chatMutedIndefinite,
    };

    if (!excludeAPICall) {
      await api.nexusUser.muteChatGlobal({
        muteChatGlobalDTO: payload,
      });
    }
  }

  async function bootstrap(callback: (user: UserResultDTO) => void): Promise<void> {
    if (!user.value) {
      throw new Error('User should already be loaded');
    }

    isMuted.value = checkIsMuted(
      user.value?.chatMutedIndefinite,
      user.value?.chatMutedUntil,
    );

    if (isMuted.value) {
      muteInterval.value = startMuteInterval();
    }

    callback(user.value);
  }

  const isPinned = computed(() => {
    return (conversationId: string): boolean => {
      if (!user.value) {
        return false;
      }

      if (!user.value.pinnedConversations) {
        return false;
      }

      const hasConversation = user.value.pinnedConversations.find(
        pinnedConversation =>
          pinnedConversation.conversationId === conversationId,
      );

      return !!hasConversation;
    };
  });

  function pinConversation(conversationId: string): void {
    if (!user.value) {
      return;
    }

    if (!user.value.pinnedConversations) {
      user.value.pinnedConversations = [];
    }

    const hasConversation = user.value.pinnedConversations.find(
      pinnedConversation =>
        pinnedConversation.conversationId === conversationId,
    );

    if (hasConversation) {
      return;
    }

    user.value.pinnedConversations.push({ conversationId });
  }

  function unpinConversation(conversationId: string): void {
    if (!user.value) {
      return;
    }

    if (!user.value.pinnedConversations) {
      user.value.pinnedConversations = [];
    }

    const conversationIndex = user.value.pinnedConversations.findIndex(
      pinnedConversation =>
        pinnedConversation.conversationId === conversationId,
    );

    if (conversationIndex === -1) {
      return;
    }

    user.value.pinnedConversations.splice(conversationIndex, 1);
  }

  return {
    keycloak,
    user,
    token,
    tokenParsed,
    isPinned,
    getUserId,
    isMuted,

    bootstrap,
    pinConversation,
    unpinConversation,
    setGlobalMute,
  };
});
